import { PaginationFilterRequest } from '@/models/core.model';
import { utcToJSDate } from '@/utils/date-time';
import { UserLookup } from './user.model';

export function purchaseFromJson(json: any): Purchase {
  return {
    ...json,
    createdAt: utcToJSDate(json.createdAt)!,
  } as Purchase;
}

/** A completed sale: a buyer paid points for one seller's item. */
export interface Purchase {
  id: number;
  buyerId: number;
  buyer?: UserLookup;
  sellerId: number;
  seller?: UserLookup;
  feature: SellableFeature;
  itemId: number;
  /** The price as listed at the moment of purchase. */
  listedPrice: number;
  /** Whole points actually charged (the listed price, floored). */
  pricePaid: number;
  /** Points burned by the platform. */
  commissionAmount: number;
  /** Points credited to the seller. */
  sellerAmount: number;
  createdAt: Date;
}

export class PurchasePaginationFilter extends PaginationFilterRequest {
  buyerId?: number;
  sellerId?: number;
  feature?: SellableFeature;
  itemId?: number;
  fromDate?: Date;
  toDate?: Date;
}

/** Features whose content can be sold for points. */
export enum SellableFeature {
  AdvanceShareTemplate = 1,
}

export const SellableFeatureList = [
  { value: SellableFeature.AdvanceShareTemplate, label: 'Advance Share Template' },
];

export enum CommissionType {
  /** A flat number of points taken from every sale. */
  Fixed = 1,
  /** A percentage of the sale price taken from every sale. */
  Percentage = 2,
}

export const CommissionTypeList = [
  { value: CommissionType.Fixed, label: 'Fixed' },
  { value: CommissionType.Percentage, label: 'Percentage' },
];

/** How much of each sale the platform keeps, per feature. */
export interface CommissionPolicy {
  id: number;
  feature: SellableFeature;
  type: CommissionType;
  /** Points when Fixed; a percent (0-100) when Percentage. */
  value: number;
  /** The lowest price a seller may set for this feature's items. */
  minimumPrice: number;
}

export interface CommissionPolicyUpsertRequest {
  feature?: SellableFeature;
  type?: CommissionType;
  value?: number;
  minimumPrice?: number;
}

/** Mirrors the server's rule: points are whole, so both roundings go down. */
export function calculateCommission(listedPrice: number, type: CommissionType, value: number) {
  const pricePaid = Math.floor(Math.max(0, listedPrice || 0));
  const raw = type === CommissionType.Percentage ? (pricePaid * value) / 100 : value;
  const commission = Math.min(Math.floor(Math.max(0, raw)), pricePaid);
  return { pricePaid, commission, sellerAmount: pricePaid - commission };
}
